// The words a page says an engine code in: read from the vocabulary the books' home serves
// (BM-BE7), never from a table in this package. A code the vocabulary has no words for is shown as
// served — the honest fallback — rather than dropped or guessed.

import type { BooksVocabulary, BooksWord } from './books-wire';

export interface StatusWords {
  label: string;
  /** One plain sentence for the legend; empty when none is served. */
  meaning: string;
}

export interface BooksWords {
  /** The kind of organization these books are kept for. */
  organizationKind: string;
  /** The chart's name, or its id as served. */
  rulePack(rulePackId: string): string;
  status(code: string): StatusWords;
  accountType(code: string): string;
  /** A statement section, read with its account type: one section can span types. */
  section(section: string, accountType: string): string;
  netAssetClass(code: string): string;
  category(code: string): string;
  partIxColumn(code: string): string;
  rollForward(key: string): string;
  /** An entry's kind; empty for one that reads as nothing (an ordinary entry). */
  entryKind(code: string): string;
  /** Where an entry came from: the namespace of its source event (its id up to the first colon). */
  source(sourceEventId: string | null | undefined): string;
  axis(code: string): string;
  scope(code: string): string;
  withheld(reason: string): StatusWords;
  reserveNoteField(field: string): string;
  total(key: string): string;
  /** The axes as the vocabulary orders them, for a breakdown that names its axes. */
  axisOrder: readonly string[];
  /** The axis whose members are the functional categories, as served; null when there is none. */
  functionalAxisId: string | null;
}

function lookup(words: readonly BooksWord[] | undefined): Map<string, BooksWord> {
  return new Map((words ?? []).map((word) => [word.code, word]));
}

function labelOf(map: Map<string, BooksWord>, code: string): string {
  return map.get(code)?.label ?? code;
}

export function booksWords(vocabulary: BooksVocabulary): BooksWords {
  const statuses = lookup(vocabulary.period_statuses);
  const accountTypes = lookup(vocabulary.account_types);
  const classes = lookup(vocabulary.net_asset_classes);
  const categories = lookup(vocabulary.functional_categories);
  const columns = lookup(vocabulary.part_ix_columns);
  const rollForward = lookup(vocabulary.roll_forward);
  const kinds = lookup(vocabulary.entry_kinds);
  const sources = lookup(vocabulary.sources);
  const axes = lookup(vocabulary.dimension_axes);
  const scopes = lookup(vocabulary.scopes);
  const withheld = lookup(vocabulary.withheld_reasons);
  const noteFields = lookup(vocabulary.reserve_note_fields);
  const totals = lookup(vocabulary.totals);
  const sections = new Map(
    (vocabulary.sections ?? []).map((word) => [`${word.account_type}/${word.section}`, word.label]),
  );
  const rulePack = vocabulary.rule_pack ?? null;
  return {
    organizationKind: vocabulary.organization_kind?.label ?? '',
    rulePack: (rulePackId) => (rulePack?.code === rulePackId ? rulePack.label : rulePackId),
    status: (code) => {
      const word = statuses.get(code);
      return { label: word?.label ?? code, meaning: word?.meaning ?? '' };
    },
    accountType: (code) => labelOf(accountTypes, code),
    section: (section, accountType) => sections.get(`${accountType}/${section}`) ?? section,
    netAssetClass: (code) => labelOf(classes, code),
    category: (code) => labelOf(categories, code),
    partIxColumn: (code) => labelOf(columns, code),
    rollForward: (key) => labelOf(rollForward, key),
    entryKind: (code) => kinds.get(code)?.label ?? code,
    source: (sourceEventId) => {
      if (sourceEventId == null || sourceEventId === '') return '';
      const namespace = sourceEventId.split(':', 1)[0] ?? '';
      return labelOf(sources, namespace);
    },
    axis: (code) => labelOf(axes, code),
    scope: (code) => labelOf(scopes, code),
    withheld: (reason) => {
      const word = withheld.get(reason);
      return { label: word?.label ?? reason, meaning: word?.meaning ?? '' };
    },
    reserveNoteField: (field) => labelOf(noteFields, field),
    total: (key) => labelOf(totals, key),
    axisOrder: (vocabulary.dimension_axes ?? []).map((word) => word.code),
    functionalAxisId: vocabulary.functional_axis_id ?? null,
  };
}
